import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "components/ui/dialog";
import { cn } from "lib/utils";
import type { RootState } from "redux/store";
import { RoomWeekResponse, useGetRoomWeekQuery } from "api/roomWeek/roomWeekApiSlice";
import {
  BookTarget,
  buildDays,
  DEFAULT_TIME_ZONE,
  roomWeekZone,
  GridView,
  initialView,
  SlotChoice,
  weekRange,
} from "../weekGrid";
import { bookingLinkFor } from "../bookingLink";
import { buildAppUrl } from "util/index";
import type { BookingQuery } from "modules/bookLocation/common/url/bookingQuerySchema";
import { useBookingWindow } from "modules/bookLocation/common/hooks/useBookingWindow";
import RoomWeekGrid from "./RoomWeekGrid";

/**
 * One room's week, to plan from and book into (M3, #3036).
 *
 * A chosen slot opens the existing booking flow prefilled (`bookingLinkFor`).
 *
 * Desktop shows the whole week; a phone shows one day at a time with a day
 * switcher — seven days of three segment columns do not fit a phone.
 */

export interface RoomWeekViewProps {
  groupId: string;
  /** First week shown, and on a phone its day; defaults to today. */
  initialDate?: Date;
  /** What the customer already entered in the flow, carried into the booking link (#3069). */
  carry?: Pick<BookingQuery, "numberOfGuests" | "eventType">;
  /**
   * Where a chosen slot's booking opens: in this window (default), or in a
   * new one — the public plan (#3075) sits in an iframe on the tenant's
   * website, where the booking flow would be cramped and cut off.
   */
  bookIn?: "thisWindow" | "newWindow";
}

type ErrorShape = { status?: number | string; data?: { message?: string } };

/** Which message a failure shows — a failure must never look like an empty week. */
const errorMessageKey = (error: ErrorShape | undefined): string | null => {
  if (error?.status === 404) return "room_week.error_not_found";
  if (error?.status === "FETCH_ERROR") return "room_week.error_unreachable";
  return null;
};

/** The view in effect: the one chosen for THIS room, else the room's initial view. */
const effectiveView = (
  chosen: { groupId: string; view: GridView } | null,
  groupId: string,
  data: RoomWeekResponse | undefined
): GridView => {
  if (chosen?.groupId === groupId) return chosen.view;
  return data ? initialView(data) : { mode: "whole" };
};

/** The heading: the segment in the segment view, else the hall or room. */
const headingFor = (data: RoomWeekResponse, view: GridView): string => {
  const s = data.structure;
  if (s.kind !== "divided") return data.name ?? "";
  if (view.mode === "segment") {
    const segment = s.segments.find((x) => x.id === view.segmentId);
    if (segment) return segment.name;
  }
  return s.whole.name ?? data.name ?? "";
};

const RoomWeekView: React.FC<RoomWeekViewProps> = ({ groupId, initialDate, carry, bookIn = "thisWindow" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const viewerId = useSelector((state: RootState) => state.account?.profile?.id ?? null);

  const [anchor, setAnchor] = React.useState<Date>(() => initialDate ?? new Date());
  // The room's location zone (#3219). The week is requested before the
  // response says which zone the room is in, so it is requested in the zone
  // last known (the default at first) and, when the response names another,
  // requested again in that one — every day, hour row and link of the grid is
  // then built in the zone the week was fetched in.
  const [timeZone, setTimeZone] = React.useState<string>(DEFAULT_TIME_ZONE);
  const { from, to } = React.useMemo(() => weekRange(anchor, timeZone), [anchor, timeZone]);

  // `currentData`, not `data`: RTK keeps the previous argument's result in
  // `data` while the next week loads, which would draw the new week's days
  // over last week's bookings — an occupied week shown as free and clickable.
  const { currentData: data, isFetching, error } = useGetRoomWeekQuery(
    { groupId, from: from.toISOString(), to: to.toISOString(), viewerId },
    { skip: !groupId }
  );

  // The view belongs to the room it was chosen for: a different groupId
  // starts from that room's own initial view, without an effect resetting it.
  const [chosenView, setChosenView] = React.useState<{ groupId: string; view: GridView } | null>(null);
  const view = effectiveView(chosenView, groupId, data);
  const setView = (next: GridView) => setChosenView({ groupId, view: next });

  const responseZone = data ? roomWeekZone(data) : null;
  React.useEffect(() => {
    if (responseZone && responseZone !== timeZone) setTimeZone(responseZone);
  }, [responseZone, timeZone]);

  // A phone opens on the first day shown — the chosen day, else today
  // (0 = Monday) — on the default clock: the room's zone is not known before
  // its week loads.
  const [mobileDayIndex, setMobileDayIndex] = React.useState(
    () => moment.tz(initialDate ?? new Date(), DEFAULT_TIME_ZONE).isoWeekday() - 1
  );
  const [pending, setPending] = React.useState<SlotChoice | null>(null);

  const days = React.useMemo(
    () => (data ? buildDays(from, data.openingHours, timeZone) : []),
    [data, from, timeZone]
  );
  // The tenant's booking window — the same reader the booking flow uses to
  // grey out dates, so the grid never offers a slot the flow would refuse.
  // The API stays the authority (ADR-0020).
  const bookingWindow = useBookingWindow(data?.useCase ?? "venue", timeZone);
  const notBeforeMs = Math.max(Date.now(), bookingWindow.earliest.valueOf());
  const notAfterMs = bookingWindow.latest.valueOf();

  const book = (target: BookTarget, choice: SlotChoice) => {
    setPending(null);
    if (!data) return;
    const link = bookingLinkFor(data, target, choice.start, choice.end, timeZone, carry);
    if (bookIn === "newWindow") window.open(buildAppUrl(link), "_blank", "noopener");
    else navigate(link);
  };

  if (!data && !error) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-foreground/60">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  if (error || !data) {
    // A failure must never look like an empty week: say which one it is.
    const e = error as ErrorShape | undefined;
    const key = errorMessageKey(e);
    // Otherwise the server's own message, so the cause is on screen.
    const message = key ? t(key) : e?.data?.message || t("room_week.error_generic");
    return (
      <div className="p-6 text-sm text-foreground" role="alert">
        {message}
      </div>
    );
  }

  const structure = data.structure;
  const title = headingFor(data, view);

  const weekLabel = `${moment.tz(from, timeZone).format("DD.MM.")} – ${moment
    .tz(to, timeZone)
    .subtract(1, "day")
    .format("DD.MM.YYYY")}`;

  const shiftWeek = (weeks: number) =>
    setAnchor(moment.tz(from, timeZone).add(weeks, "week").add(12, "hour").toDate());

  const mobileDay = days[mobileDayIndex] ? [days[mobileDayIndex]] : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-foreground">{title}</h1>
          {data.location.name && (
            <p className="truncate text-sm text-foreground/60">{data.location.name}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => shiftWeek(-1)} aria-label={t("room_week.previous_week")}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[9rem] text-center text-sm text-foreground">{weekLabel}</span>
          <Button variant="ghost" size="icon" onClick={() => shiftWeek(1)} aria-label={t("room_week.next_week")}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())}>
            {t("room_week.this_week")}
          </Button>
          {isFetching && <Loader2 className="ml-1 h-4 w-4 animate-spin text-foreground/60" />}
        </div>
      </div>

      {structure.kind === "divided" && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {view.mode === "segment" ? (
            <Button variant="outline" size="sm" onClick={() => setView({ mode: "whole" })}>
              {t("room_week.back_to_hall", { hall: structure.whole.name ?? "" })}
            </Button>
          ) : (
            <span className="text-foreground/60">{t("room_week.hall_hint")}</span>
          )}
        </div>
      )}

      <Legend
        showContext={view.mode === "segment"}
        signedIn={viewerId !== null}
        namesOtherOrganization={data.intervals.some((i) => i.kind === "other_organization")}
        ownWindows={(data.windows ?? []).some((w) => w.own && !w.released)}
        otherWindows={(data.windows ?? []).some((w) => !w.own)}
      />

      {/* Desktop: the whole week */}
      <div className="hidden overflow-x-auto md:block">
        <div className={cn(structure.kind === "divided" && view.mode === "whole" ? "min-w-[56rem]" : "min-w-[40rem]")}>
          <RoomWeekGrid
            data={data}
            days={days}
            view={view}
            notBeforeMs={notBeforeMs}
            notAfterMs={notAfterMs}
            onSlotClick={setPending}
            onOpenSegment={(segmentId) => setView({ mode: "segment", segmentId })}
            timeZone={timeZone}
          />
        </div>
      </div>

      {/* Phone: one day at a time */}
      <div className="md:hidden">
        <div className="mb-2 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            disabled={mobileDayIndex === 0}
            onClick={() => setMobileDayIndex((i) => Math.max(0, i - 1))}
            aria-label={t("room_week.previous_day")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-foreground">
            {mobileDay[0] ? t(`common.weekdays_full.${mobileDay[0].weekday}`) : ""}
          </span>
          <Button
            variant="ghost"
            size="icon"
            disabled={mobileDayIndex >= days.length - 1}
            onClick={() => setMobileDayIndex((i) => Math.min(days.length - 1, i + 1))}
            aria-label={t("room_week.next_day")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <RoomWeekGrid
          data={data}
          days={mobileDay}
          view={view}
          notBeforeMs={notBeforeMs}
          notAfterMs={notAfterMs}
          onSlotClick={setPending}
          onOpenSegment={(segmentId) => setView({ mode: "segment", segmentId })}
          timeZone={timeZone}
        />
      </div>

      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogTitle>{t("room_week.book_dialog_title")}</DialogTitle>
          {pending && (
            <>
              <DialogDescription>
                {moment.tz(pending.start, timeZone).format("dddd, DD.MM.YYYY, H:mm")} –{" "}
                {moment.tz(pending.end, timeZone).format("H:mm")}
              </DialogDescription>
              <div className="mt-2 flex flex-col gap-2">
                {pending.targets.map((target) => (
                  <Button
                    key={`${target.scope}-${target.groupId}`}
                    variant={target.scope === "segment" ? "default" : "outline"}
                    onClick={() => book(target, pending)}
                  >
                    {target.scope === "segment"
                      ? t("room_week.book_segment", { segment: target.name })
                      : t("room_week.book_whole", { room: target.name })}
                  </Button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Legend: React.FC<{
  showContext: boolean;
  signedIn: boolean;
  namesOtherOrganization: boolean;
  ownWindows: boolean;
  otherWindows: boolean;
}> = ({ showContext, signedIn, namesOtherOrganization, ownWindows, otherWindows }) => {
  const { t } = useTranslation();
  // Signed out, a booking is never shown as own or an organisation's. Another
  // organisation appears by name only where it opted in (D3b), for signed-out
  // and signed-in callers alike — so signed out, its colour is explained only
  // when the answer actually carries one.
  const items: { className: string; label: string }[] = [
    { className: "bg-background border border-foreground/20", label: t("room_week.legend_free") },
    ...(signedIn
      ? [
          { className: "bg-primary", label: t("room_week.legend_own") },
          { className: "bg-primary/30", label: t("room_week.legend_organization") },
        ]
      : []),
    ...(signedIn || namesOtherOrganization
      ? [{ className: "bg-foreground/20", label: t("room_week.legend_other_organization") }]
      : []),
    { className: "bg-foreground/10", label: t("room_week.legend_occupied") },
    // Allocation windows (W2-1), explained only when the week has any.
    ...(ownWindows
      ? [{ className: "border border-dashed border-primary bg-primary/5", label: t("room_week.legend_window_own") }]
      : []),
    ...(otherWindows
      ? [
          {
            className:
              "bg-[repeating-linear-gradient(45deg,rgba(var(--color-foreground),0.20)_0_3px,rgba(var(--color-foreground),0.06)_3px_6px)]",
            label: t("room_week.legend_window_other"),
          },
        ]
      : []),
  ];
  if (showContext) {
    items.push({
      className:
        "bg-[repeating-linear-gradient(135deg,rgba(var(--color-foreground),0.15)_0_3px,transparent_3px_6px)]",
      label: t("room_week.legend_other_segment"),
    });
  }
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/70">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span className={cn("inline-block h-3 w-3 rounded-[2px]", item.className)} />
          {item.label}
        </li>
      ))}
    </ul>
  );
};

export default RoomWeekView;
